import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { getApiBase, getToken } from './utils'

export const api = axios.create()

api.interceptors.request.use((config) => {
  if (!config.baseURL) {
    config.baseURL = getApiBase()
  }
  const tok = getToken()
  if (tok) {
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${tok}`
  }
  return config
})

// Auto-refresh handling
let isRefreshing = false
let pendingQueue: Array<{resolve: (value: unknown) => void, reject: (reason?: any) => void, config: AxiosRequestConfig}> = []

async function refreshAccessToken(): Promise<string> {
  // FastAPI-Users n'utilise pas de refresh tokens
  // En cas d'expiration du token, on redirige vers login
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user')
  window.location.href = '/login'
  throw new Error('Token expired - redirecting to login')
}

function processQueue(error: any, token: string | null) {
  pendingQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error)
    } else {
      // retry the original request with new token
      if (token && config && config.headers) {
        (config.headers as any)['Authorization'] = `Bearer ${token}`
      }
      resolve(api.request(config))
    }
  })
  pendingQueue = []
}

// Debug + auto refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    try {
      if ((response?.config?.url || '').includes('/chat')) {
        // eslint-disable-next-line no-console
        console.info('[API] /chat response', response.status, response.data)
      }
    } catch {}
    return response
  },
  async (error: AxiosError) => {
    try {
      const url = (error?.config as any)?.url || ''
      const status = error?.response?.status

      // If Unauthorized, try to refresh once
      if (status === 401) {
        const originalRequest = error.config as AxiosRequestConfig
        if (!originalRequest) throw error

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            pendingQueue.push({ resolve, reject, config: originalRequest })
          })
        }

        isRefreshing = true
        try {
          const newToken = await refreshAccessToken()
          processQueue(null, newToken)
          isRefreshing = false
          // Retry current request
          originalRequest.headers = originalRequest.headers || {}
          ;(originalRequest.headers as any)['Authorization'] = `Bearer ${newToken}`
          return api.request(originalRequest)
        } catch (e) {
          processQueue(e, null)
          isRefreshing = false
          // clear tokens and reject
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          return Promise.reject(e)
        }
      }

      if (url.includes('/chat')) {
        // eslint-disable-next-line no-console
        console.error('[API] /chat error', status, error?.response?.data || error?.message)
      } else {
        // eslint-disable-next-line no-console
        console.error('[API] error', url, status, error?.response?.data || error?.message)
      }
    } catch {}
    return Promise.reject(error)
  }
)

// ============================================================================
// System Prompts API (Admin Only)
// ============================================================================

export interface SystemPrompt {
  id: number
  name: string
  slug: string
  description: string | null
  content: string
  is_default: boolean
  is_active: boolean
  version: number
  created_at: string
  updated_at: string
  created_by: number | null
  updated_by: number | null
}

export interface SystemPromptListItem {
  id: number
  name: string
  slug: string
  description: string | null
  is_default: boolean
  is_active: boolean
  version: number
  content_preview: string
  updated_at: string
}

export interface SystemPromptHistory {
  id: number
  prompt_id: number
  content: string
  version: number
  changed_by: number | null
  changed_at: string
  change_reason: string | null
}

export interface SystemPromptCreate {
  name: string
  description?: string | null
  content: string
  is_default?: boolean
}

export interface SystemPromptUpdate {
  name?: string
  description?: string | null
  content?: string
  is_default?: boolean
  is_active?: boolean
  change_reason?: string
}

export const systemPromptsApi = {
  list: async (includeInactive = false): Promise<{ items: SystemPromptListItem[]; total: number }> => {
    const res = await api.get('/system-prompts', { params: { include_inactive: includeInactive } })
    return res.data
  },

  get: async (id: number): Promise<SystemPrompt> => {
    const res = await api.get(`/system-prompts/${id}`)
    return res.data
  },

  create: async (data: SystemPromptCreate): Promise<SystemPrompt> => {
    const res = await api.post('/system-prompts', data)
    return res.data
  },

  update: async (id: number, data: SystemPromptUpdate): Promise<SystemPrompt> => {
    const res = await api.put(`/system-prompts/${id}`, data)
    return res.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/system-prompts/${id}`)
  },

  getHistory: async (id: number, limit = 50): Promise<{ items: SystemPromptHistory[]; total: number }> => {
    const res = await api.get(`/system-prompts/${id}/history`, { params: { limit } })
    return res.data
  },

  restore: async (id: number, version: number): Promise<SystemPrompt> => {
    const res = await api.post(`/system-prompts/${id}/restore/${version}`)
    return res.data
  },
}
